import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../../../../components/modal/Dialog";
import { IconType, Button, ListItemIconTile } from "@sellar-io/components";
import { twMerge } from "tailwind-merge";
import { ReactNode } from "react";
import dayjs from "dayjs";
import { Alert } from "~/components/alert/Alert";
import { useNavigate } from "react-router";
import { SELLAR_CREDIT_STATEMENT } from "~/constants/routes";
import { useSellarCreditStatementStore } from "~/pages/sellarCredit/stores/SellarCreditStatementStore";
import { withSearchParams } from "~/utils/withSearchParams";
import { useFirebaseUser } from "../../../../../../hooks/useFirebaseUser.shared";
import { useGetTwoEarliestUnpaidStatementMonth } from "../hooks/useGetTwoEarliestUnpaidStatementMonth";

export type TwoSellarCreditPayDownTabDialogProps = {
  active: boolean;
  onDismiss: () => void;
};

export const TwoSellarCreditPayDownTabDialog = ({
  active,
  onDismiss,
}: TwoSellarCreditPayDownTabDialogProps) => {
  const navigate = useNavigate();
  const setDocumentMonth = useSellarCreditStatementStore(
    (state) => state.setDocumentMonth,
  );
  const { user } = useFirebaseUser();
  const { mutateAsync: getTwoEarliestUnpaidStatementMonth, isPending } =
    useGetTwoEarliestUnpaidStatementMonth();

  const navigateToStatementMonth = async () => {
    if (isPending) {
      return;
    }

    const fallbackMonth = dayjs().format("YYYY-MM");
    let earliestUnpaidMonth = fallbackMonth;

    if (!active || !user) {
      setDocumentMonth(earliestUnpaidMonth);
      navigate(withSearchParams(SELLAR_CREDIT_STATEMENT, { provider: "two" }));
      onDismiss();
      return;
    }

    try {
      const result = await getTwoEarliestUnpaidStatementMonth();

      if (result.documentMonth) {
        earliestUnpaidMonth = result.documentMonth;
      }
    } catch (error) {
      console.error(error);
    }

    setDocumentMonth(earliestUnpaidMonth);
    navigate(withSearchParams(SELLAR_CREDIT_STATEMENT, { provider: "two" }));
    onDismiss();
  };

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        title="Pay down your Sellar Credit tab"
        description="You can pay down your Sellar Credit tab by paying for individual orders or entire billing statements"
      />
      <DialogContent>
        <div className="flex flex-col gap-2">
          <PayDownTabMethodOption
            icon="CART"
            title="Pay off an individual order"
            description="Use the 'pay' button next to any dispatched order on this page to make a one-off payment"
            disabled={isPending}
            onClick={onDismiss}
          />

          <PayDownTabMethodOption
            icon="FILE_DOCUMENT_OUTLINE"
            title="Pay off a statement"
            description="View your most recently issued Sellar Credit monthly statement and make a one-off payment"
            disabled={isPending}
            onClick={() => {
              void navigateToStatementMonth();
            }}
          />
          <Alert
            title="Do you already have a Direct Debit setup?"
            message={
              <>
                If you already setup a Direct Debit to pay off your Sellar
                Credit tab each month, you can still make a one-off repayment
                earlier than your statement is due. However, we do not recommend
                making a one-off repayment within a week of the due date. Your
                Direct Debit may already be in progress.
              </>
            }
            className="bg-zinc-50"
          />

          <div className="mt-2 flex w-full justify-end gap-2">
            <Button
              color={"secondary"}
              className={"p-4"}
              onClick={() => {
                const url =
                  import.meta.env.VITE_ENV === "production"
                    ? `https://buyer.two.inc/statements`
                    : `https://buyer.sandbox.two.inc/statements`;

                window.open(url, "_blank");
              }}
            >
              View Statements
            </Button>

            <Button color={"primary"} className={"p-4"} onClick={onDismiss}>
              Got it
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

type PayDownTabMethodOptionProps = {
  icon: IconType;
  title: string;
  description: string;
  tag?: ReactNode;
  footer?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
};

const PayDownTabMethodOption = ({
  icon,
  title,
  description,
  tag,
  footer,
  disabled,
  onClick,
}: PayDownTabMethodOptionProps) => {
  return (
    <div className="flex flex-col gap-2">
      <div
        onClick={disabled ? undefined : onClick}
        className={twMerge(
          "flex flex-col justify-between gap-4 rounded-lg border border-zinc-200 bg-white p-4 sm:flex-row sm:items-center",
          onClick && !disabled && "cursor-pointer",
          disabled && "cursor-not-allowed opacity-60",
        )}
      >
        <div className="flex flex-1 items-center gap-2">
          <ListItemIconTile icon={icon} />
          <div className="flex flex-col">
            <p className="font-semibold">
              {title} {Boolean(tag) && tag}
            </p>
            <p className="text-sm">{description}</p>
          </div>
        </div>
      </div>
      {Boolean(footer) && footer}
    </div>
  );
};
